import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { apiGet, apiPost } from '../lib/api';
import { useTranslation } from '../i18n';
import logo from '../assets/logo-wordmark.png';
import './ChoosePlanScreen.css';

/**
 * First-install plan picker — full-page takeover shown while
 * access-state === 'no_plan_selected' (a fresh install that has never
 * accepted a Shopify billing charge). There is no skip and no exit:
 * every other API 402s until a plan's charge is accepted.
 *
 * Everything on the cards comes from GET /api/billing/plans (the
 * read-only plan-quotas config + backend/services/plan-copy.service):
 *   - prices / trial days / quotas  → `plans[tier]` (never hardcoded)
 *   - tagline, 6 key numbers, ≤4 highlights, "Everything in X, plus:"
 *     → `plans[tier].copy` (localised by ?locale; the i18n `plans.tiers`
 *     block is only the fallback for a backend without those fields)
 *   - the contact-based Custom one-liner → `customPlan`
 *   - the "Compare all features" table → `comparison`
 *
 * Layout: compact header, FOUR cards in one row (CTA right under the
 * price so names, prices, trial chips and CTAs are visible without
 * scrolling at 1366×768 in the admin frame), Custom as a slim line
 * under the grid, compare modal, trust row, one-line share-insights
 * consent, terms. Choosing a plan POSTs /api/billing/select-plan and
 * breaks out of the embedded iframe to Shopify's consent screen via
 * window.top.location — after approval Shopify redirects to
 * /api/billing/callback and the app reloads into the dashboard.
 *
 * `preview` (dev-only route /dev/plan-picker) renders the same screen
 * with real API data but inert CTAs and no telemetry.
 */

const TERMS_URL = 'https://tryautonoma.app/terms';
const PRIVACY_URL = 'https://tryautonoma.app/privacy';
export const SUPPORT_EMAIL = 'autonoma@ontechnologiesptyltd.com';
export const SUPPORT_HREF = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent('Autonoma subscription')}`;

/**
 * The "Share anonymised insights" checkbox starts CHECKED. Mirrors the
 * backend's new-shop default (store.service.applyNewShopDefaults) so
 * both sides say "on by default"; the merchant's submitted value is
 * what gets stored.
 */
export const SHARE_INSIGHTS_DEFAULT = true;

/** The 6 "Key numbers" rows, in card order — identical on every card. */
export const KEY_NUMBER_ROWS = [
  'productBuilds', 'imageAds', 'videoAds', 'advertorials', 'splitTests', 'findProductsSearches',
];

/** Fallback derivation of a key number from `plan.features` (old backends). */
const KEY_NUMBER_FROM_FEATURES = {
  productBuilds: (f) => f.productsPerMonth,
  imageAds: (f) => f.imageAdsPerMonth,
  videoAds: (f) => (f.videoAds ? f.videosPerMonth : null),
  advertorials: (f) => f.advertorialsPerMonth,
  splitTests: (f) => (f.splitTests ? f.splitTestsPerMonth : null),
  findProductsSearches: (f) => f.findProductsPerMonth,
};

export const HIGHLIGHTS_MAX = 4;

const fill = (template, vars) => Object.entries(vars)
  .reduce((s, [k, v]) => s.split(`{${k}}`).join(String(v)), String(template));

const normaliseQuota = (v) => {
  if (v === 'unlimited' || v === Infinity) return 'unlimited';
  return typeof v === 'number' && v > 0 ? v : null;
};

/** `—` (muted) when a feature isn't in the plan, `∞` for unlimited. */
export function formatQuota(value, language) {
  const v = normaliseQuota(value);
  if (v === null) return '—';
  if (v === 'unlimited') return '∞';
  try { return v.toLocaleString(language || undefined); } catch { return String(v); }
}

/** Server `copy.keyNumbers` when present, else derived from `features`. */
export function keyNumbersFor(plan, t) {
  const server = plan?.copy?.keyNumbers;
  if (Array.isArray(server) && server.length === KEY_NUMBER_ROWS.length) {
    return server.map((row) => ({ key: row.key, label: row.label, value: normaliseQuota(row.value) }));
  }
  const f = plan?.features || {};
  return KEY_NUMBER_ROWS.map((key) => ({
    key,
    label: t(`plans.keyNumbers.${key}`),
    value: normaliseQuota(KEY_NUMBER_FROM_FEATURES[key](f)),
  }));
}

export function taglineFor(planKey, plan, t) {
  const server = plan?.copy?.tagline;
  if (typeof server === 'string' && server) return server;
  const local = t(`plans.tiers.${planKey}.tagline`);
  return typeof local === 'string' && !local.startsWith('plans.') ? local : '';
}

export function highlightsFor(planKey, plan, t) {
  const server = plan?.copy?.highlights;
  const list = Array.isArray(server) && server.length
    ? server.map((h) => (typeof h === 'string' ? { key: h, text: h } : h))
    : (Array.isArray(t(`plans.tiers.${planKey}.highlights`))
      ? t(`plans.tiers.${planKey}.highlights`).map((text, i) => ({ key: `${planKey}${i + 1}`, text }))
      : []);
  return list.slice(0, HIGHLIGHTS_MAX);
}

/** "Everything in {previous}, plus:" — null on the first tier. */
export function inheritsFor(planKey, plan, orderedKeys, plans, t) {
  const server = plan?.copy?.inheritsFrom;
  if (server && server.label) return server.label;
  if (server === null) return null;
  const idx = orderedKeys.indexOf(planKey);
  if (idx <= 0) return null;
  const prev = plans[orderedKeys[idx - 1]];
  return prev ? fill(t('plans.inherits'), { name: prev.name }) : null;
}

/** "Starter and Growth include a 3-day free trial. Cancel anytime." */
export function subtitleFor(plans, trialUsed, t) {
  const trialTiers = Object.values(plans || {}).filter((p) => !trialUsed && (p.trialDays || 0) > 0);
  if (!trialTiers.length) return t('plans.subtitleNoTrial');
  const names = trialTiers.map((p) => p.name);
  const list = names.length > 1
    ? `${names.slice(0, -1).join(', ')} ${t('plans.andPlans')} ${names[names.length - 1]}`
    : names[0];
  return fill(t('plans.subtitle'), { plans: list, n: trialTiers[0].trialDays });
}

function Check({ className }) {
  return (
    <svg className={className} viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <path d="M3 8.5l3 3 7-7" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function TrialChip({ hasTrial, trialDays, t, className = '' }) {
  return hasTrial ? (
    <span className={`au-pp-chip au-pp-chip--trial ${className}`} data-testid="plan-chip-trial">
      {fill(t('plans.trialChip'), { n: trialDays })}
    </span>
  ) : (
    <span className={`au-pp-chip au-pp-chip--billed ${className}`} data-testid="plan-chip-billed">
      {t('plans.billedToday')}
    </span>
  );
}

/**
 * One plan card. Shared with the plan-limit overlay (PlanLimitOverlay),
 * which adds `current` (the shop's plan: marked, CTA disabled) and
 * `allowance` (that feature's monthly allowance on this plan, e.g.
 * "40 product builds / month", shown under the price).
 */
export function PlanCard({ planKey, plan, orderedKeys, plans, trialUsed, busy, onChoose, t, language, reactivate = false, current = false, allowance = null }) {
  const recommended = plan.recommended === true;
  const hasTrial = !trialUsed && (plan.trialDays || 0) > 0;
  const tagline = taglineFor(planKey, plan, t);
  const numbers = keyNumbersFor(plan, t);
  const highlights = highlightsFor(planKey, plan, t);
  const inherits = inheritsFor(planKey, plan, orderedKeys, plans, t);
  const copySource = plan.copy && Array.isArray(plan.copy.keyNumbers) ? 'server' : 'client';
  const ctaLabel = current
    ? t('plans.currentPlan')
    : busy === planKey
      ? t('plans.opening')
      : reactivate
        ? fill(t('plans.reactivateCta'), { name: plan.name })
        : (hasTrial ? t('plans.startTrial') : fill(t('plans.choose'), { name: plan.name }));

  return (
    <div
      data-testid={`plan-card-${planKey}`}
      data-copy-source={copySource}
      data-recommended={recommended || undefined}
      data-current={current || undefined}
      className={`au-pp-card${recommended ? ' au-pp-card--recommended' : ''}${current ? ' au-pp-card--current' : ''}`}
    >
      {current
        ? <span className="au-pp-badge au-pp-badge--current" data-testid="plan-badge-current">{t('plans.currentPlan')}</span>
        : recommended && <span className="au-pp-badge">{t('plans.mostPopular')}</span>}

      {/* a. name + tagline */}
      <div className="au-pp-card__head">
        <h3 className="au-pp-card__name">{plan.name}</h3>
        {tagline && <p className="au-pp-card__tagline" data-testid="plan-tagline">{tagline}</p>}
      </div>

      {/* b. price + trial chip (or the feature allowance in the overlay) */}
      <div>
        <div className="au-pp-card__price">
          <span className="au-pp-card__amount">${plan.price}</span>
          <span className="au-pp-card__per">{t('plans.perMonth')}</span>
        </div>
        {allowance
          ? <span className="au-pp-chip au-pp-chip--allowance" data-testid="plan-allowance">{allowance}</span>
          : <TrialChip hasTrial={hasTrial} trialDays={plan.trialDays} t={t} />}
      </div>

      {/* c. CTA — right here, under the price */}
      <div>
        <button
          type="button"
          data-testid={`plan-cta-${planKey}`}
          className={`au-pp-cta ${recommended && !current ? 'au-pp-cta--primary' : 'au-pp-cta--outline'}${(busy && busy !== planKey) || current ? ' au-pp-cta--dimmed' : ''}`}
          onClick={() => onChoose(planKey)}
          disabled={!!busy || current}
          aria-disabled={current || undefined}
        >
          {ctaLabel}
        </button>
      </div>

      {/* d. key numbers — 6 identical rows on every card */}
      <div className="au-pp-numbers" data-testid="plan-key-numbers">
        <span className="au-pp-numbers__title">{t('plans.keyNumbersTitle')}</span>
        <dl>
          {numbers.map((row) => (
            <div key={row.key} className="au-pp-numbers__row" data-key-number={row.key}>
              <dt>{row.label}</dt>
              <dd
                className={row.value === null ? 'au-pp-numbers__none' : undefined}
                aria-label={row.value === null ? t('plans.notIncluded') : undefined}
              >
                {formatQuota(row.value, language)}
              </dd>
            </div>
          ))}
        </dl>
      </div>

      {/* e. "Everything in X, plus:" (reserved line on the first tier) + highlights */}
      <div>
        <p className="au-pp-inherits" data-testid="plan-inherits" aria-hidden={inherits ? undefined : 'true'}>
          {inherits || '\u00a0'}
        </p>
        <ul className="au-pp-highlights" data-testid="plan-highlights">
          {highlights.map((h) => (
            <li key={h.key} data-highlight={h.key}>
              <Check className="au-pp-check" />
              <span>{h.text}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function SkeletonCard() {
  return (
    <div className="au-pp-skeleton" data-testid="plan-skeleton" aria-hidden="true">
      <span /><span /><span /><span /><span /><span /><span /><span /><span /><span />
    </div>
  );
}

/** Full comparison table: 4 tiers + Custom, sticky header, scrolls inside the modal. */
function CompareModal({ comparison, trialUsed, onClose, t, language }) {
  const titleId = 'autonoma-compare-title';
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  const columns = comparison.columns || [];
  const custom = comparison.custom || null;

  const cell = (row, value) => {
    if (row.type === 'bool') {
      return value ? <Check className="au-pp-table__yes" /> : <span className="au-pp-table__no" aria-label={t('plans.notIncluded')}>—</span>;
    }
    if (value === null || value === undefined || value === false) {
      return <span className="au-pp-table__no" aria-label={t('plans.notIncluded')}>—</span>;
    }
    if (row.type === 'money') return <span className="au-pp-table__num">{typeof value === 'number' ? `$${value}` : value}</span>;
    if (row.type === 'number') return <span className="au-pp-table__num">{typeof value === 'number' || value === 'unlimited' ? formatQuota(value, language) : value}</span>;
    return value === true ? <Check className="au-pp-table__yes" /> : <span>{value}</span>;
  };

  return createPortal(
    <div className="au-pp-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid="plan-compare-modal">
      <div className="au-pp-modal" role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <div className="au-pp-modal__head">
          <h2 id={titleId}>{comparison.title || t('plans.compare')}</h2>
          <button type="button" className="au-pp-modal__close" onClick={onClose} aria-label={t('plans.close')}>×</button>
        </div>
        <div className="au-pp-modal__body">
          <table className="au-pp-table">
            <thead>
              <tr>
                <th scope="col" aria-label={comparison.perMonth || ''} />
                {columns.map((c) => (
                  <th key={c.key} scope="col" className={c.recommended ? 'au-pp-table__recommended' : undefined}>
                    <span className="au-pp-table__plan">{c.name}</span>
                    <span className="au-pp-table__price">${c.price}{t('plans.perMonth')}</span>
                    <TrialChip hasTrial={!trialUsed && (c.trialDays || 0) > 0} trialDays={c.trialDays} t={t} className="au-pp-table__chip" />
                  </th>
                ))}
                {custom && (
                  <th scope="col">
                    <span className="au-pp-table__plan">{custom.name}</span>
                    <span className="au-pp-table__price">{custom.priceLabel}</span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {(comparison.groups || []).map((group) => (
                <React.Fragment key={group.key}>
                  <tr className="au-pp-table__group">
                    <td colSpan={1 + columns.length + (custom ? 1 : 0)}>{group.label}</td>
                  </tr>
                  {group.rows.map((row) => (
                    <tr key={row.key} data-compare-row={row.key}>
                      <th scope="row" style={{ fontWeight: 500 }}>{row.label}</th>
                      {columns.map((c) => (
                        <td key={c.key} className={c.recommended ? 'au-pp-table__recommended' : undefined}>
                          {cell(row, row.values ? row.values[c.key] : null)}
                        </td>
                      ))}
                      {custom && <td>{cell(row, row.custom)}</td>}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * `mode`:
 *   'choose'     — first install, no plan ever accepted (default).
 *   'reactivate' — cancelled / frozen / declined / expired / pending: same
 *                  cards (Settings → Billing "view and reactivate"), a
 *                  reason line from the access state, reactivate CTAs.
 * `onRecheck` — forces a fresh Shopify verification (the post-approval
 * return and the "Check again" button); provided by AppLayout.
 */
export default function ChoosePlanScreen({ preview = false, mode = 'choose', message = null, reason = null, onRecheck = null }) {
  const { t, language } = useTranslation();
  const reactivate = mode === 'reactivate';
  const [rechecking, setRechecking] = useState(false);
  const [data, setData] = useState(null);        // { plans, customPlan, comparison, trialUsed }
  const [busy, setBusy] = useState(null);        // planKey being submitted
  const [error, setError] = useState(null);
  const [compareOpen, setCompareOpen] = useState(false);
  const [consentOpen, setConsentOpen] = useState(false);
  // "Share anonymised insights" — ON BY DEFAULT (pre-checked). The
  // merchant can uncheck it here or turn it off any time in Settings;
  // whatever the box says at submit rides on the select-plan POST and is
  // what the backend stores (unchecked → false, left checked → true).
  // This is anonymised, aggregated usage data — never personal or
  // store-identifying data (GDPR/CCPA framing; see the privacy policy).
  const [shareConsent, setShareConsent] = useState(SHARE_INSIGHTS_DEFAULT);
  // Shopify bounced them back after a declined/abandoned consent screen —
  // or the callback could not verify the charge with Shopify (unverified).
  const billingHint = new URLSearchParams(window.location.search).get('autonoma_billing');
  const declined = billingHint === 'declined';
  const unverified = billingHint === 'unverified';

  const recheck = async () => {
    if (!onRecheck || rechecking) return;
    setRechecking(true);
    try { await onRecheck(); } finally { setRechecking(false); }
  };
  const viewTracked = useRef(false);

  // Fire-and-forget telemetry through the existing events pipeline
  // (POST /api/billing/plan-picker-event → events.logEvent). Never
  // blocks or surfaces errors; skipped entirely in the dev preview.
  const track = useCallback((event, extra = {}) => {
    if (preview) return;
    Promise.resolve()
      .then(() => apiPost('/api/billing/plan-picker-event', {
        event, locale: language, viewportWidth: window.innerWidth, ...extra,
      }))
      .catch(() => {});
  }, [preview, language]);

  useEffect(() => {
    // `locale` localises the server-driven card copy (en/es/fr/de/pt).
    apiGet(`/api/billing/plans${language ? `?locale=${encodeURIComponent(language)}` : ''}`)
      .then((r) => r.json())
      .then((d) => setData({
        plans: d.plans || {},
        customPlan: d.customPlan || null,
        comparison: d.comparison || null,
        trialUsed: d.trialUsed === true,
      }))
      .catch(() => setError(t('plans.loadError')));
  }, [t, language]);

  useEffect(() => {
    if (viewTracked.current) return;
    viewTracked.current = true;
    track('plan_picker_view');
  }, [track]);

  const plans = data?.plans || null;
  const orderedKeys = useMemo(() => Object.keys(plans || {}), [plans]);

  const choose = async (planKey) => {
    track('plan_cta_click', { tier: planKey });
    if (preview) return;
    setBusy(planKey);
    setError(null);
    try {
      const r = await apiPost('/api/billing/select-plan', { planKey, dataSharingConsent: shareConsent });
      const d = await r.json();
      if (!r.ok || !d.confirmationUrl) throw new Error(d.error || 'Could not start the subscription');
      // Shopify's consent screen cannot render inside the embedded
      // iframe — break out at the top window.
      (window.top || window).location.href = d.confirmationUrl;
    } catch (e) {
      setError(e.message || t('plans.genericError'));
      setBusy(null);
    }
  };

  const openCompare = () => {
    track('plan_compare_open');
    setCompareOpen(true);
  };
  const closeCompare = useCallback(() => setCompareOpen(false), []);

  const customPlan = data?.customPlan;
  const comparison = data?.comparison;

  return (
    <div className="au-pp" data-preview={preview || undefined}>
      <div className="au-pp__inner">
        <header className="au-pp-header">
          <div className="au-pp-header__title">
            <span className="au-pp-logo" aria-hidden="true"><img src={logo} alt="" /></span>
            <h1>{reactivate ? t('plans.reactivateTitle') : t('plans.title')}</h1>
          </div>
          {reactivate ? (
            <p className="au-pp-subtitle" data-testid="plan-reactivate-reason">
              {(() => {
                const key = `plans.reactivateReason.${reason || 'inactive'}`;
                const local = t(key);
                const reasonText = typeof local === 'string' && !local.startsWith('plans.') ? local : t('plans.reactivateReason.inactive');
                return `${reasonText} ${t('plans.reactivateSubtitle')}`;
              })()}
            </p>
          ) : (
            <p className="au-pp-subtitle" data-testid="plan-subtitle">
              {plans ? subtitleFor(plans, data.trialUsed, t) : '\u00a0'}
            </p>
          )}
          {reactivate && message && (
            <p className="au-pp-subtitle" data-testid="plan-reactivate-message" style={{ marginTop: 4 }}>{message}</p>
          )}
        </header>

        {declined && (
          <div role="alert" className="au-pp-banner">{t('plans.declined')}</div>
        )}
        {unverified && (
          <div role="alert" className="au-pp-banner" data-testid="plan-unverified">
            {t('plans.unverified')}
            {onRecheck && (
              <>
                {' '}
                <button type="button" className="au-pp-compare" data-testid="plan-recheck" onClick={recheck} disabled={rechecking} style={{ marginLeft: 8 }}>
                  {rechecking ? t('plans.checking') : t('plans.checkAgain')}
                </button>
              </>
            )}
          </div>
        )}
        {error && (
          <div role="alert" className="au-pp-banner">{error}</div>
        )}

        {!plans && !error && (
          <div className="au-pp-grid" data-testid="plan-cards-loading" aria-busy="true" aria-label={t('plans.loading')}>
            <SkeletonCard /><SkeletonCard /><SkeletonCard /><SkeletonCard />
          </div>
        )}

        {plans && (
          <div className="au-pp-grid" data-testid="plan-cards">
            {orderedKeys.map((key) => (
              <PlanCard
                key={key}
                planKey={key}
                plan={plans[key]}
                orderedKeys={orderedKeys}
                plans={plans}
                trialUsed={data.trialUsed}
                busy={busy}
                onChoose={choose}
                t={t}
                language={language}
                reactivate={reactivate}
              />
            ))}
          </div>
        )}

        {/* Custom (contact-based) — one slim line, never a Shopify charge. */}
        {plans && customPlan?.cta?.href && (
          <p className="au-pp-custom" data-testid="plan-custom-line">
            {customPlan.oneLiner || t('plans.customLine')}{' '}
            <a href={customPlan.cta.href} data-testid="plan-custom-cta">{customPlan.cta.label || t('plans.customCta')}</a>
          </p>
        )}

        {plans && comparison && (
          <div>
            <button type="button" className="au-pp-compare" data-testid="plan-compare-open" onClick={openCompare}>
              {t('plans.compare')}
            </button>
          </div>
        )}

        <p className="au-pp-trust" data-testid="plan-trust-row">{t('plans.trustRow')}</p>
        <p className="au-pp-trust" data-testid="plan-support-row">
          <a href={SUPPORT_HREF} data-testid="plan-contact-support">{t('plans.contactSupport')}</a>
          {onRecheck && !unverified && (
            <>
              {' · '}
              <button type="button" className="au-pp-compare" data-testid="plan-recheck-inline" onClick={recheck} disabled={rechecking} style={{ padding: 0 }}>
                {rechecking ? t('plans.checking') : t('plans.checkAgain')}
              </button>
            </>
          )}
        </p>

        {/* ── Share anonymised insights (on by default — uncheck to opt out) ── */}
        <div data-testid="share-insights-card" className="au-pp-consent">
          <div className="au-pp-consent__row">
            <input
              type="checkbox"
              id="autonoma-share-consent"
              data-testid="share-insights-checkbox"
              checked={shareConsent}
              onChange={(e) => setShareConsent(e.target.checked)}
            />
            <label htmlFor="autonoma-share-consent">{t('plans.consentTitle')}</label>
            <button
              type="button"
              className="au-pp-info"
              data-testid="share-insights-info"
              aria-label={t('plans.consentInfo')}
              aria-expanded={consentOpen}
              aria-controls="autonoma-share-consent-info"
              onClick={() => setConsentOpen((o) => !o)}
            >
              i
            </button>
          </div>
          {consentOpen && (
            <div id="autonoma-share-consent-info" className="au-pp-consent__info" data-testid="share-insights-info-body">
              <span>{t('plans.consentBody')}</span>
              <span>{t('plans.consentDefault')}</span>
            </div>
          )}
        </div>

        <p className="au-pp-terms">
          {t('plans.billingNote')}{' '}
          <a href={TERMS_URL} target="_blank" rel="noopener noreferrer">{t('plans.terms')}</a>
          {' '}{t('plans.and')}{' '}
          <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">{t('plans.privacy')}</a>.
        </p>
      </div>

      {compareOpen && comparison && (
        <CompareModal comparison={comparison} trialUsed={data.trialUsed} onClose={closeCompare} t={t} language={language} />
      )}
    </div>
  );
}
